import { AuthService } from './authService';
import {
  PagedResult,
  POrderLineWithHeaderDto,
  POrderLinesQueryParams
} from '../types/purchaseOrderLines';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

/**
 * Service for managing Purchase Order Lines (Bestillingslinje)
 * Provides methods for fetching paginated, filtered, and sorted order lines with header information
 */
class PurchaseOrderLinesService {

  /**
   * Get paginated purchase order lines with header information
   * Supports server-side filtering, sorting, and pagination
   *
   * @param params Query parameters for pagination, filtering, and sorting
   * @returns Promise with paginated result containing order lines
   * @throws Error if the request fails
   *
   * @example
   * const result = await purchaseOrderLinesService.getPOrderLinesPaged({
   *   pageNumber: 1,
   *   pageSize: 50,
   *   sortColumn: 'OrderNo',
   *   sortDirection: 'desc',
   *   itemNo: '12345',
   *   supplierNo: 'SUP001',
   *   maxOrderStatus: 40
   * });
   */
  async getPOrderLinesPaged(
    params: POrderLinesQueryParams = {}
  ): Promise<PagedResult<POrderLineWithHeaderDto>> {
    // Build query string from parameters
    const queryParams = new URLSearchParams();

    // Pagination
    if (params.pageNumber !== undefined) {
      queryParams.append('pageNumber', params.pageNumber.toString());
    }
    if (params.pageSize !== undefined) {
      queryParams.append('pageSize', params.pageSize.toString());
    }

    // Sorting
    if (params.sortColumn) {
      queryParams.append('sortColumn', params.sortColumn);
    }
    if (params.sortDirection) {
      queryParams.append('sortDirection', params.sortDirection);
    }

    // Filtering
    if (params.itemNo) {
      queryParams.append('itemNo', params.itemNo);
    }
    if (params.supplierNo) {
      queryParams.append('supplierNo', params.supplierNo);
    }
    if (params.fromDate) {
      const fromDateStr = typeof params.fromDate === 'string'
        ? params.fromDate
        : params.fromDate.toISOString();
      queryParams.append('fromDate', fromDateStr);
    }
    if (params.toDate) {
      const toDateStr = typeof params.toDate === 'string'
        ? params.toDate
        : params.toDate.toISOString();
      queryParams.append('toDate', toDateStr);
    }
    if (params.maxOrderStatus !== undefined) {
      queryParams.append('maxOrderStatus', params.maxOrderStatus.toString());
    }

    // Build final URL
    const url = `${API_BASE_URL}/porders/lines${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    // Make authenticated request
    const response = await AuthService.authenticatedGet(url);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: response.statusText
      }));
      throw new Error(
        errorData.error ||
        `Failed to get purchase order lines: ${response.status} ${response.statusText}`
      );
    }

    return await response.json();
  }

  /**
   * Get purchase order lines with default active orders filter (status < 60)
   * Convenient method for fetching only active/pending orders
   *
   * @param params Query parameters (maxOrderStatus will be set to 50 if not provided)
   * @returns Promise with paginated result containing active order lines
   *
   * @example
   * const result = await purchaseOrderLinesService.getActiveOrderLines({
   *   pageNumber: 1,
   *   pageSize: 100,
   *   itemNo: '12345'
   * });
   */
  async getActiveOrderLines(
    params: Omit<POrderLinesQueryParams, 'maxOrderStatus'> = {}
  ): Promise<PagedResult<POrderLineWithHeaderDto>> {
    return this.getPOrderLinesPaged({
      ...params,
      maxOrderStatus: 50 // Exclude exported orders (status 60)
    });
  }

  /**
   * Get purchase order lines by item number
   * Shorthand method for filtering by item number
   *
   * @param itemNo Item number to filter by
   * @param additionalParams Additional query parameters
   * @returns Promise with paginated result
   *
   * @example
   * const result = await purchaseOrderLinesService.getByItemNo('12345', {
   *   pageSize: 25,
   *   sortColumn: 'DeliveryDate'
   * });
   */
  async getByItemNo(
    itemNo: string,
    additionalParams: Omit<POrderLinesQueryParams, 'itemNo'> = {}
  ): Promise<PagedResult<POrderLineWithHeaderDto>> {
    return this.getPOrderLinesPaged({
      ...additionalParams,
      itemNo
    });
  }

  /**
   * Get purchase order lines by supplier number
   * Shorthand method for filtering by supplier
   *
   * @param supplierNo Supplier number to filter by
   * @param additionalParams Additional query parameters
   * @returns Promise with paginated result
   *
   * @example
   * const result = await purchaseOrderLinesService.getBySupplier('SUP001', {
   *   maxOrderStatus: 40
   * });
   */
  async getBySupplier(
    supplierNo: string,
    additionalParams: Omit<POrderLinesQueryParams, 'supplierNo'> = {}
  ): Promise<PagedResult<POrderLineWithHeaderDto>> {
    return this.getPOrderLinesPaged({
      ...additionalParams,
      supplierNo
    });
  }

  /**
   * Get purchase order lines within a date range
   * Shorthand method for filtering by order date range
   *
   * @param fromDate Start date (inclusive)
   * @param toDate End date (inclusive)
   * @param additionalParams Additional query parameters
   * @returns Promise with paginated result
   *
   * @example
   * const result = await purchaseOrderLinesService.getByDateRange(
   *   new Date('2025-01-01'),
   *   new Date('2025-01-31')
   * );
   */
  async getByDateRange(
    fromDate: Date | string,
    toDate: Date | string,
    additionalParams: Omit<POrderLinesQueryParams, 'fromDate' | 'toDate'> = {}
  ): Promise<PagedResult<POrderLineWithHeaderDto>> {
    return this.getPOrderLinesPaged({
      ...additionalParams,
      fromDate,
      toDate
    });
  }

  /**
   * Format date to Norwegian locale
   *
   * @param date Date string or null/undefined
   * @returns Formatted date string or '-'
   */
  formatDate(date: string | null | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('no-NO');
  }

  /**
   * Format date and time to Norwegian locale
   *
   * @param date Date string or null/undefined
   * @returns Formatted date/time string or '-'
   */
  formatDateTime(date: string | null | undefined): string {
    if (!date) return '-';
    return new Date(date).toLocaleString('no-NO');
  }

  /**
   * Format currency value
   *
   * @param value Numeric value or null/undefined
   * @param currency Currency code (default: 'NOK')
   * @returns Formatted currency string
   */
  formatCurrency(value: number | null | undefined, currency: string = 'NOK'): string {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('no-NO', {
      style: 'currency',
      currency: currency
    }).format(value);
  }

  /**
   * Format quantity value
   *
   * @param value Numeric value or null/undefined
   * @param decimals Number of decimal places (default: 2)
   * @returns Formatted quantity string
   */
  formatQuantity(value: number | null | undefined, decimals: number = 2): string {
    if (value === null || value === undefined) return '-';
    return value.toFixed(decimals);
  }

  /**
   * Calculate completion percentage for an order line
   *
   * @param quantity Total quantity ordered
   * @param quantityDelivered Quantity delivered
   * @returns Completion percentage (0-100)
   */
  calculateCompletionPercentage(
    quantity: number | null | undefined,
    quantityDelivered: number | null | undefined
  ): number {
    if (!quantity || quantity === 0) return 0;
    const delivered = quantityDelivered || 0;
    return Math.min(Math.round((delivered / quantity) * 100), 100);
  }
}

/**
 * Singleton instance of PurchaseOrderLinesService
 * Use this exported instance throughout the application
 */
export const purchaseOrderLinesService = new PurchaseOrderLinesService();
